import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

/**
 * Renders a DOM element to a PDF File using html2canvas + jsPDF.
 * Forces element to render at A4 width (794px) to prevent text wrapping.
 */
export async function renderElementToPdfFile(elementId, fileName) {
  const RENDER_SCALE = 1.7
  const IMAGE_QUALITY = 1

  const element = document.getElementById(elementId)
  if (!element) throw new Error(`Element #${elementId} not found in DOM`)

  // Step 1: temporarily show all hidden ancestors
  const hiddenAncestors = []
  let node = element.parentElement
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node)
    if (
      cs.display === 'none' ||
      cs.visibility === 'hidden' ||
      node.style.display === 'none'
    ) {
      hiddenAncestors.push({
        el: node,
        display: node.style.display,
        visibility: node.style.visibility,
        position: node.style.position,
        left: node.style.left,
        top: node.style.top,
        zIndex: node.style.zIndex,
        width: node.style.width,
        minWidth: node.style.minWidth,
        overflow: node.style.overflow,
      })
      node.style.display = 'block'
      node.style.visibility = 'visible'
      node.style.position = 'absolute'
      node.style.left = '-99999px'
      node.style.top = '0'
      node.style.zIndex = '-1'
      node.style.width = '794px' // FIX: was 1000px
      node.style.minWidth = '794px' // FIX: was 1000px
      node.style.overflow = 'visible'
    }
    node = node.parentElement
  }

  // Force element to A4 width
  const orig = {
    display: element.style.display,
    visibility: element.style.visibility,
    width: element.style.width,
    maxWidth: element.style.maxWidth,
    minWidth: element.style.minWidth,
    boxSizing: element.style.boxSizing,
  }
  element.style.display = 'block'
  element.style.visibility = 'visible'
  element.style.width = '794px'
  element.style.maxWidth = '794px'
  element.style.minWidth = '794px'
  element.style.boxSizing = 'border-box'

  await new Promise(resolve => setTimeout(resolve, 250))

  let canvas
  try {
    canvas = await html2canvas(element, {
      scale: RENDER_SCALE,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 794,
      height: element.scrollHeight,
      windowWidth: 794, // FIX: was 1400 — must match element width
      windowHeight: element.scrollHeight, // FIX: removed extra +400
    })
  } finally {
    // Restore element styles
    element.style.display = orig.display
    element.style.visibility = orig.visibility
    element.style.width = orig.width
    element.style.maxWidth = orig.maxWidth
    element.style.minWidth = orig.minWidth
    element.style.boxSizing = orig.boxSizing

    // Restore ancestor styles
    hiddenAncestors.forEach(
      ({
        el,
        display,
        visibility,
        position,
        left,
        top,
        zIndex,
        width,
        minWidth,
        overflow,
      }) => {
        el.style.display = display
        el.style.visibility = visibility
        el.style.position = position
        el.style.left = left
        el.style.top = top
        el.style.zIndex = zIndex
        el.style.width = width
        el.style.minWidth = minWidth
        el.style.overflow = overflow
      }
    )
  }

  // Step 2: Trim trailing whitespace from canvas
  // Finds the last row with meaningful ink and cuts canvas height there (+buffer).
  // This prevents blank pages caused by excess padding/margin at bottom of element.
  const canvasCtx = canvas.getContext('2d')
  const TRIM_SAMPLE_STEP = Math.max(1, Math.floor(canvas.width / 900))
  const TRIM_INK_THRESHOLD = 0.01
  const TRIM_BOTTOM_BUFFER_PX = 40

  const effectiveCanvasHeight = (() => {
    for (let y = canvas.height - 1; y >= 0; y -= 2) {
      const rowData = canvasCtx.getImageData(0, y, canvas.width, 1).data
      let inkCount = 0
      let total = 0
      for (let x = 0; x < canvas.width; x += TRIM_SAMPLE_STEP) {
        const i = x * 4
        if (rowData[i] < 250 || rowData[i + 1] < 250 || rowData[i + 2] < 250) {
          inkCount++
        }
        total++
      }
      if (total > 0 && inkCount / total > TRIM_INK_THRESHOLD) {
        return Math.min(canvas.height, y + TRIM_BOTTOM_BUFFER_PX)
      }
    }
    return canvas.height
  })()

  // Step 3: canvas → multi-page A4 PDF
  const PDF_W = 210,
    PDF_H = 297
  const NEXT_PAGE_TOP_MARGIN_MM = 10
  const canvasWmm = (canvas.width / (96 * RENDER_SCALE)) * 25.4
  const canvasHmm = (effectiveCanvasHeight / (96 * RENDER_SCALE)) * 25.4
  const ratio = PDF_W / canvasWmm
  const scaledH = canvasHmm * ratio
  const DOM_TO_CANVAS_SCALE =
    effectiveCanvasHeight / Math.max(1, element.scrollHeight)

  // Find a safer page-break row (prefer whitespace) near target cut line.
  const WHITE_PIXEL_MIN = 250
  const WHITE_ROW_RATIO = 0.995
  const WHITE_BAND_HALF_WINDOW = 2
  const WHITE_BAND_MIN_COUNT = 4
  const BACKWARD_WHITE_SEARCH_PX = 220
  const DOM_BREAK_SEARCH_BACKWARD_PX = 2000
  const MIN_SLICE_PX = 120
  const MIN_PAGE_CONTENT_PX = 40
  const PAGE_OVERLAP_PX = 0
  const SAFETY_BACKTRACK_PX = 10
  const FORCE_EARLY_CUT_PX = 36
  const FINAL_CUT_BACKTRACK_MAX_PX = 70
  const FINAL_FALLBACK_WINDOW_PX = 40
  const DIACRITIC_GUARD_PX = 4
  const MIN_REMAINING_PX_FOR_NEW_PAGE = 24
  const SAMPLE_STEP_X = Math.max(1, Math.floor(canvas.width / 900))

  const collectDomBreakpoints = rootElement => {
    if (!rootElement) return []

    const rootRect = rootElement.getBoundingClientRect()
    const selectors =
      'p, li, table, tr, ul, ol, figure, h1, h2, h3, h4, h5, h6, blockquote, pre, .rich-content > div, .comment-item > p'
    const nodes = rootElement.querySelectorAll(selectors)
    const pointSet = new Set()

    nodes.forEach(nodeEl => {
      const rect = nodeEl.getBoundingClientRect()
      if (!rect || rect.height < 10) return

      const topCss = rect.top - rootRect.top
      const top = Math.round(topCss * DOM_TO_CANVAS_SCALE)
      if (top > 0 && top < effectiveCanvasHeight) {
        pointSet.add(top)
      }
    })

    // Add line-level breakpoints from text nodes to avoid cutting through a line.
    const walker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: textNode => {
          if (!textNode || !textNode.nodeValue) {
            return NodeFilter.FILTER_REJECT
          }
          return textNode.nodeValue.trim().length > 0
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT
        },
      }
    )

    let textNode = walker.nextNode()
    while (textNode) {
      const range = document.createRange()
      range.selectNodeContents(textNode)
      const rects = range.getClientRects()
      for (let i = 0; i < rects.length; i += 1) {
        const rect = rects[i]
        if (!rect || rect.height < 6) continue

        const topCss = rect.top - rootRect.top
        const top = Math.round(topCss * DOM_TO_CANVAS_SCALE)
        if (top > 0 && top < effectiveCanvasHeight) {
          pointSet.add(top)
        }
      }
      textNode = walker.nextNode()
    }

    return Array.from(pointSet).sort((a, b) => a - b)
  }

  const collectProtectedBlocks = rootElement => {
    if (!rootElement) return []

    const rootRect = rootElement.getBoundingClientRect()
    const selectors = 'table, ul, ol, figure, img, pre, blockquote'
    const nodes = rootElement.querySelectorAll(selectors)
    const blocks = []

    nodes.forEach(nodeEl => {
      const rect = nodeEl.getBoundingClientRect()
      if (!rect || rect.height < 16) return

      const topCss = rect.top - rootRect.top
      const bottomCss = rect.bottom - rootRect.top
      const top = Math.max(0, Math.round(topCss * DOM_TO_CANVAS_SCALE))
      const bottom = Math.min(
        effectiveCanvasHeight,
        Math.round(bottomCss * DOM_TO_CANVAS_SCALE)
      )

      if (bottom - top >= 16) {
        blocks.push({ top, bottom })
      }
    })

    return blocks.sort((a, b) => a.top - b.top)
  }

  const domBreakpoints = collectDomBreakpoints(element)
  const protectedBlocks = collectProtectedBlocks(element)

  const findCrossingProtectedBlock = (startY, endY, pageCapacityPx) => {
    const minTop = startY + MIN_PAGE_CONTENT_PX

    for (let i = 0; i < protectedBlocks.length; i += 1) {
      const block = protectedBlocks[i]
      if (block.bottom <= minTop) continue
      if (block.top >= endY) break

      const crossesBoundary = block.top < endY && block.bottom > endY
      if (!crossesBoundary) continue

      const blockHeight = block.bottom - block.top
      const canFitOnePage = blockHeight <= pageCapacityPx
      if (!canFitOnePage) {
        continue
      }

      if (block.top > minTop) {
        return block
      }
    }

    return null
  }

  const findDomBreakY = (startY, targetEndY, maxY) => {
    if (!domBreakpoints.length) return null

    const minBreakY = Math.min(maxY - 1, startY + MIN_PAGE_CONTENT_PX)
    const idealBreakY = Math.min(targetEndY, maxY - 1)
    const lowerBound = Math.max(
      minBreakY,
      idealBreakY - DOM_BREAK_SEARCH_BACKWARD_PX
    )

    let chosen = null
    for (let i = domBreakpoints.length - 1; i >= 0; i -= 1) {
      const pointY = domBreakpoints[i]
      if (pointY > idealBreakY) continue
      if (pointY < lowerBound) break
      chosen = pointY
      break
    }

    if (chosen === null) return null
    return Math.max(minBreakY, chosen - DIACRITIC_GUARD_PX)
  }

  const findAnyPreviousDomBreakY = (startY, targetEndY, maxY) => {
    if (!domBreakpoints.length) return null

    const minBreakY = Math.min(maxY - 1, startY + MIN_PAGE_CONTENT_PX)
    const idealBreakY = Math.min(targetEndY, maxY - 1)

    for (let i = domBreakpoints.length - 1; i >= 0; i -= 1) {
      const pointY = domBreakpoints[i]
      if (pointY > idealBreakY) continue
      if (pointY < minBreakY) break
      return pointY
    }

    return null
  }

  const isMostlyWhiteRow = y => {
    if (!canvasCtx) return false
    const rowY = Math.max(0, Math.min(effectiveCanvasHeight - 1, y))
    const rowData = canvasCtx.getImageData(0, rowY, canvas.width, 1).data
    let whiteCount = 0
    let sampleCount = 0

    for (let x = 0; x < canvas.width; x += SAMPLE_STEP_X) {
      const idx = x * 4
      const r = rowData[idx]
      const g = rowData[idx + 1]
      const b = rowData[idx + 2]
      if (
        r >= WHITE_PIXEL_MIN &&
        g >= WHITE_PIXEL_MIN &&
        b >= WHITE_PIXEL_MIN
      ) {
        whiteCount += 1
      }
      sampleCount += 1
    }

    return sampleCount > 0 && whiteCount / sampleCount >= WHITE_ROW_RATIO
  }

  const getRowInkRatio = y => {
    if (!canvasCtx) return 1
    const rowY = Math.max(0, Math.min(effectiveCanvasHeight - 1, y))
    const rowData = canvasCtx.getImageData(0, rowY, canvas.width, 1).data
    let inkCount = 0
    let sampleCount = 0

    for (let x = 0; x < canvas.width; x += SAMPLE_STEP_X) {
      const idx = x * 4
      const r = rowData[idx]
      const g = rowData[idx + 1]
      const b = rowData[idx + 2]
      if (r < WHITE_PIXEL_MIN || g < WHITE_PIXEL_MIN || b < WHITE_PIXEL_MIN) {
        inkCount += 1
      }
      sampleCount += 1
    }

    return sampleCount > 0 ? inkCount / sampleCount : 1
  }

  const isWhiteBand = y => {
    let whiteRows = 0
    for (
      let yy = y - WHITE_BAND_HALF_WINDOW;
      yy <= y + WHITE_BAND_HALF_WINDOW;
      yy += 1
    ) {
      if (isMostlyWhiteRow(yy)) whiteRows += 1
    }
    return whiteRows >= WHITE_BAND_MIN_COUNT
  }

  const hasMeaningfulInkInRange = (startY, endY) => {
    const from = Math.max(0, Math.floor(startY))
    const to = Math.min(effectiveCanvasHeight - 1, Math.ceil(endY))
    if (to <= from) return false

    const STEP_Y = 2
    const INK_THRESHOLD = 0.01
    for (let y = from; y <= to; y += STEP_Y) {
      if (getRowInkRatio(y) > INK_THRESHOLD) return true
    }
    return false
  }

  const finalizeCutY = (startY, cutY) => {
    const minCutY = Math.min(effectiveCanvasHeight - 1, startY + MIN_SLICE_PX)
    const originalY = Math.max(
      minCutY,
      Math.min(effectiveCanvasHeight - 1, cutY)
    )

    // Hard rule: if there is a previous DOM line/block boundary, cut there.
    const strictDomBreakY = findAnyPreviousDomBreakY(
      startY,
      originalY,
      effectiveCanvasHeight
    )
    if (strictDomBreakY !== null) {
      return Math.max(minCutY, strictDomBreakY)
    }

    let y = originalY

    if (isWhiteBand(y)) {
      return Math.max(minCutY, y - DIACRITIC_GUARD_PX)
    }

    const lowerBound = Math.max(minCutY, y - FINAL_CUT_BACKTRACK_MAX_PX)
    while (y > lowerBound) {
      y -= 1
      if (isWhiteBand(y)) {
        return Math.max(minCutY, y - DIACRITIC_GUARD_PX)
      }
    }

    // If still no clean band, select the least-ink row in a short backward window.
    const fallbackLowerBound = Math.max(
      minCutY,
      originalY - FINAL_FALLBACK_WINDOW_PX
    )
    let bestY = Math.max(minCutY, originalY - SAFETY_BACKTRACK_PX)
    let bestInk = Number.MAX_SAFE_INTEGER

    for (let yy = originalY; yy >= fallbackLowerBound; yy -= 1) {
      const ink = getRowInkRatio(yy)
      if (ink < bestInk) {
        bestInk = ink
        bestY = yy
      }
    }

    return bestY
  }

  const adjustCutForProtectedBlocks = (startY, cutY) => {
    const minCutY = Math.min(effectiveCanvasHeight - 1, startY + 1)
    const originalCut = Math.max(
      minCutY,
      Math.min(effectiveCanvasHeight - 1, cutY)
    )

    for (let i = 0; i < protectedBlocks.length; i += 1) {
      const block = protectedBlocks[i]
      const insideBlock =
        originalCut > block.top + 1 && originalCut < block.bottom - 1
      if (!insideBlock) continue

      // If a page break falls inside a protected block (especially table),
      // move the whole block to the next page by cutting before the block starts.
      if (block.top > startY + 1) {
        return Math.max(minCutY, block.top - 1)
      }
    }

    return originalCut
  }

  const findSafeBreakY = (startY, targetEndY, maxY) => {
    const domBreakY = findDomBreakY(startY, targetEndY, maxY)
    if (domBreakY !== null) return domBreakY

    const anyPreviousDomBreakY = findAnyPreviousDomBreakY(
      startY,
      targetEndY,
      maxY
    )
    if (anyPreviousDomBreakY !== null) return anyPreviousDomBreakY

    const minBreakY = Math.min(maxY, startY + MIN_SLICE_PX)
    const idealBreakY = Math.min(targetEndY, maxY - 1)

    if (idealBreakY <= minBreakY) return idealBreakY

    const lowerBound = Math.max(
      minBreakY,
      idealBreakY - BACKWARD_WHITE_SEARCH_PX
    )

    // Backward only: if near boundary is risky, push it to next page instead of cutting forward.
    for (let y = idealBreakY; y >= lowerBound; y -= 1) {
      if (isWhiteBand(y)) {
        return Math.max(minBreakY, y - SAFETY_BACKTRACK_PX)
      }
    }

    // No safe white band found: cut earlier aggressively to avoid clipping text.
    const forcedCutY = idealBreakY - FORCE_EARLY_CUT_PX
    return Math.max(minBreakY, forcedCutY)
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  })

  let yOffsetPx = 0
  let pageIndex = 0
  while (yOffsetPx < effectiveCanvasHeight) {
    // FIX: use effectiveCanvasHeight
    if (!hasMeaningfulInkInRange(yOffsetPx, effectiveCanvasHeight - 1)) {
      // FIX
      break
    }

    const pageTopMargin = pageIndex === 0 ? 0 : NEXT_PAGE_TOP_MARGIN_MM
    const availableHeight = Math.max(PDF_H - pageTopMargin, 1)
    const targetSlicePx = (availableHeight / scaledH) * effectiveCanvasHeight // FIX
    const targetEndY = Math.min(
      effectiveCanvasHeight, // FIX
      Math.ceil(yOffsetPx + targetSlicePx)
    )
    const srcY = Math.floor(yOffsetPx)

    // FIX: if remaining content fits entirely within one page, skip all
    // page-break logic and take everything — avoids spurious cuts from
    // domBreakpoints that happen to fall near targetEndY.
    const remainingFitsOnePage = targetEndY >= effectiveCanvasHeight

    let safeEndY
    if (remainingFitsOnePage) {
      safeEndY = effectiveCanvasHeight
    } else {
      const crossingBlock = findCrossingProtectedBlock(
        srcY,
        targetEndY,
        targetSlicePx
      )

      const forcedBlockEndY = crossingBlock
        ? Math.max(srcY + 1, crossingBlock.top - 1)
        : null

      const rawEndY =
        forcedBlockEndY ||
        Math.max(
          srcY + 1,
          findSafeBreakY(srcY, targetEndY, effectiveCanvasHeight)
        )
      const finalizedEndY = finalizeCutY(srcY, rawEndY)
      safeEndY = Math.max(
        srcY + 1,
        adjustCutForProtectedBlocks(srcY, finalizedEndY)
      )
    }
    const srcH = safeEndY - srcY
    const pageH = (srcH / effectiveCanvasHeight) * scaledH // FIX

    const pc = document.createElement('canvas')
    pc.width = canvas.width
    pc.height = Math.ceil(srcH)
    const ctx = pc.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, pc.width, pc.height)
    ctx.drawImage(
      canvas,
      0,
      srcY,
      canvas.width,
      srcH,
      0,
      0,
      pc.width,
      pc.height
    )

    pdf.addImage(
      pc.toDataURL('image/jpeg', IMAGE_QUALITY),
      'JPEG',
      0,
      pageTopMargin,
      PDF_W,
      pageH
    )
    const nextOffset = safeEndY - PAGE_OVERLAP_PX
    yOffsetPx = nextOffset > srcY ? nextOffset : safeEndY
    pageIndex += 1
    const remainingPx = effectiveCanvasHeight - yOffsetPx // FIX
    const shouldCreateNextPage =
      remainingPx > MIN_REMAINING_PX_FOR_NEW_PAGE &&
      hasMeaningfulInkInRange(yOffsetPx, effectiveCanvasHeight - 1) // FIX

    if (shouldCreateNextPage) {
      pdf.addPage()
    } else {
      break
    }
  }

  const blob = pdf.output('blob')
  return new File([blob], `${fileName}.pdf`, { type: 'application/pdf' })
}
